export const computeLoanTermYears = ({ purchasePrice, realEstateValue }) => {
  // Ensure inputs are numbers and purchasePrice is positive
  const numPurchasePrice = Number(purchasePrice);
  const numRealEstateValue = Number(realEstateValue);

  if (numPurchasePrice > 0 && numRealEstateValue >= 0) {
    const realEstateProportion = numRealEstateValue / numPurchasePrice;
    if (realEstateProportion > 0.51) {
      return 25;
    } else {
      // Ensure realEstateProportion is between 0 and 1 for the formula
      const validRealEstateProportion = Math.max(0, Math.min(1, realEstateProportion));
      const nonRealEstateProportion = 1 - validRealEstateProportion;
      const blendedTerm = (validRealEstateProportion * 25) + (nonRealEstateProportion * 10);
      return Math.round(blendedTerm); // This could be NaN if inputs are unexpected
    }
  }
  return 10; // Default term if inputs are invalid or RE value leads to default
};

export const calcGuaranteeFee = (loanAmount) => {
  const numLoanAmount = Number(loanAmount);
  if (typeof numLoanAmount !== 'number' || numLoanAmount < 0) return 0;

  const guaranteedPortion = numLoanAmount * 0.75; // 75% is SBA guaranteed

  if (numLoanAmount <= 350000) {
    // $350,000 or less (maturity >12 months): 0%
    return 0;
  } else if (numLoanAmount <= 700000) {
    // $350,001 - $700,000: 2.77% of guaranteed portion
    return guaranteedPortion * 0.0277;
  } else if (numLoanAmount <= 1000000) {
    // $700,001 - $1,000,000: 3.27% of guaranteed portion
    return guaranteedPortion * 0.0327;
  } else {
    // $1,000,001 - $5,000,000: 3.5% of guaranteed portion up to $1M + 3.75% over $1M
    const firstMillionGuaranteed = Math.min(guaranteedPortion, 750000); // 75% of $1M
    const overFirstMillionGuaranteed = Math.max(0, guaranteedPortion - 750000);
    return (firstMillionGuaranteed * 0.035) + (overFirstMillionGuaranteed * 0.0375);
  }
};

export const calcAnnualPmt = ({ principal, rate, termYears }) => {
  const numPrincipal = Number(principal);
  const numRate = Number(rate); // Expecting decimal rate, e.g., 0.075 for 7.5%
  const numTermYears = Number(termYears);

  if (numPrincipal <= 0 || numRate <= 0 || numTermYears <= 0) {
    return 0;
  }

  const monthlyRate = numRate / 12;
  const numberOfPayments = numTermYears * 12;

  if (monthlyRate === 0) { // Handle interest-free loan
    return numberOfPayments > 0 ? (numPrincipal / numberOfPayments) * 12 : 0;
  }

  // Standard loan payment formula: P * [r(1+r)^n] / [(1+r)^n – 1]
  const monthlyPayment = (numPrincipal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
  
  if (isNaN(monthlyPayment) || !isFinite(monthlyPayment)) {
      return 0; // Handle division by zero or other invalid math results
  }
  return monthlyPayment * 12;
};

export const calcDSCR = ({ noi, annualDebtService }) => {
  const numNoi = Number(noi);
  const numAnnualDebtService = Number(annualDebtService);

  if (numAnnualDebtService <= 0) { 
    return 0; 
  }
  const dscr = numNoi / numAnnualDebtService;
  return isNaN(dscr) || !isFinite(dscr) ? 0 : dscr;
};

/**
 * Calculate total project cost, equity, and loan amount.
 * Equity = 10% of total project cost (purchase price + all fees).
 * Iterates to resolve the circular dependency between guarantee fee and loan amount.
 */
export const calcProjectCost = (purchasePrice, { equityPercent = 10, packagingFee = 2500, closingCosts = 10000 } = {}) => {
  const pp = Number(purchasePrice);
  if (pp <= 0) return { totalProjectCost: 0, equity: 0, loanAmount: 0, guaranteeFee: 0, packagingFee, closingCosts };

  const loanFraction = (100 - equityPercent) / 100;

  // Iterate: total project cost includes guarantee fee which depends on loan amount
  let totalProjectCost = pp + packagingFee + closingCosts;
  for (let i = 0; i < 5; i++) {
    const loanAmount = totalProjectCost * loanFraction;
    const gf = calcGuaranteeFee(loanAmount);
    totalProjectCost = pp + gf + packagingFee + closingCosts;
  }

  const loanAmount = totalProjectCost * loanFraction;
  const guaranteeFee = calcGuaranteeFee(loanAmount);
  const equity = totalProjectCost * (equityPercent / 100);

  return {
    totalProjectCost: Math.round(totalProjectCost),
    equity: Math.round(equity),
    loanAmount: Math.round(loanAmount),
    guaranteeFee: Math.round(guaranteeFee),
    packagingFee,
    closingCosts
  };
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
}; 